import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import { useUser } from '../../contexts/UserContext';
import { analyzeATS } from '../../services/openaiService';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  CheckCircleIcon,
  AlertTriangleIcon,
  XIcon,
  SendIcon,
  SparklesIcon,
  HelpIcon,
} from '../icons';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * ATS Check — Electron-native pane.
 *
 * Two views:
 *   1. SetupView   — JD + resume choice (profile / upload / paste). Calls
 *                    analyzeATS() directly (no deduct-credit; this is free
 *                    on the website too).
 *   2. ResultsView — overall score ring, keyword match (matched + missing),
 *                    format score, checklist, improvement suggestions.
 * ─────────────────────────────────────────────────────────────────────── */

async function extractText(file) {
  const name = (file.name || '').toLowerCase();
  if (file.type === 'text/plain' || name.endsWith('.txt')) return await file.text();
  if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const c = await page.getTextContent();
      out += c.items.map(it => it.str).join(' ') + '\n';
    }
    return out.trim();
  }
  if (name.endsWith('.docx') || name.endsWith('.doc') || file.type.includes('word')) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return (r.value || '').trim();
  }
  throw new Error('Unsupported file. Use PDF, DOCX, DOC, or TXT.');
}

function buildResumeTextFromProfile(profile) {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.phone) text += `Phone: ${profile.phone}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  if (profile.github) text += `GitHub: ${profile.github}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.length) {
    text += 'WORK EXPERIENCE\n';
    profile.workExperience.forEach(exp => {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `${exp.description}\n`;
        text += '\n';
      }
    });
  }
  if (Array.isArray(profile.education) && profile.education.length) {
    text += 'EDUCATION\n';
    profile.education.forEach(edu => {
      if (edu.degree || edu.institution) {
        text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
      }
    });
    text += '\n';
  }
  if (Array.isArray(profile.projects) && profile.projects.length) {
    text += 'PROJECTS\n';
    profile.projects.forEach(proj => {
      if (proj.name) {
        text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
        if (proj.description) text += `${proj.description}\n`;
      }
    });
  }
  if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
  return text.trim();
}

const CHECKLIST_LABELS = {
  contactInfo:    'Contact Info',
  education:      'Education',
  experience:     'Work Experience',
  skills:         'Skills',
  projects:       'Projects',
  certifications: 'Certifications',
};

const CHECKLIST_TIPS = {
  contactInfo:    'Add a clear contact line — name, email, phone, location, LinkedIn.',
  education:      'List your degree(s) with institution and year — ATS uses this for filtering.',
  experience:     'Include at least one structured job entry (title, company, dates, bullets).',
  skills:         'Add a Skills section that mirrors the JD\'s technical vocabulary.',
  projects:       'Add 1–3 named projects with a one-line outcome each.',
  certifications: 'Add any relevant certifications — ATS often weight these.',
};

export default function ATSCheckPane() {
  const [view, setView] = useState('setup'); // 'setup' | 'results'
  const [result, setResult] = useState(null);
  const [meta, setMeta] = useState(null);    // { resumeText, jobDescription }

  if (view === 'setup') {
    return (
      <SetupView
        onAnalyzed={(r, m) => {
          setResult(r);
          setMeta(m);
          setView('results');
        }}
      />
    );
  }
  return (
    <ResultsView
      result={result}
      meta={meta}
      onRunAgain={() => {
        setView('setup');
      }}
      onReset={() => {
        setView('setup');
        setResult(null);
        setMeta(null);
      }}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* SetupView                                                               */
/* ─────────────────────────────────────────────────────────────────────── */

function SetupView({ onAnalyzed }) {
  const { user } = useUser();

  const [jobDescription, setJobDescription] = useState('');
  const [resumeChoice, setResumeChoice] = useState('profile'); // 'profile' | 'upload' | 'paste'
  const [pasteText, setPasteText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedText, setUploadedText] = useState('');
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [profile, setProfile] = useState({ data: null, hasResume: false, loading: true });
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const p = d?.profile;
        // ATS Check is profile-text-driven: any signal (summary, skills,
        // experience) is enough to use it. Don't require a stored resume.
        const hasUsableProfile = !!(
          p && (
            p.professionalSummary ||
            p.skills ||
            (Array.isArray(p.workExperience) && p.workExperience.some(e => e.company || e.position))
          )
        );
        setProfile({ data: p, hasResume: hasUsableProfile, loading: false });
        if (!hasUsableProfile && resumeChoice === 'profile') setResumeChoice('paste');
      } catch {
        if (!cancelled) setProfile({ data: null, hasResume: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setParseError('');
    setIsParsing(true);
    try {
      const text = await extractText(file);
      if (!text) throw new Error('No text extracted from file.');
      setUploadedText(text);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
      setUploadedText('');
    } finally {
      setIsParsing(false);
    }
  };

  const resumeText = (() => {
    if (resumeChoice === 'paste')   return pasteText.trim();
    if (resumeChoice === 'upload')  return uploadedText.trim();
    if (resumeChoice === 'profile') return buildResumeTextFromProfile(profile.data).trim();
    return '';
  })();

  const canStart = jobDescription.trim().length > 0 && resumeText.length > 0 && !isParsing;
  const gate = useFeatureGate('atsCheck');

  const handleAnalyze = async () => {
    if (!canStart) {
      if (!jobDescription.trim()) setError('Paste the job description first.');
      else if (!resumeText)        setError('Add your resume — pick a profile, upload, or paste.');
      return;
    }
    if (gate.locked) { gate.openPaywall(); return; }
    setAnalyzing(true);
    setError('');
    try {
      // Deduct before the OpenAI call so a free-tier user can't burn
      // model cost beyond their allowance. Shared gate also updates the
      // per-feature counter chip and opens the paywall on 403.
      const { ok, reason } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') { setAnalyzing(false); return; }
        throw new Error(reason || 'Out of ATS Check credits — upgrade to continue.');
      }
      const r = await analyzeATS(resumeText, jobDescription.trim());
      if (!r) throw new Error('No analysis returned.');
      onAnalyzed(r, { resumeText, jobDescription: jobDescription.trim() });
    } catch (err) {
      setError(err.message || 'Failed to analyze. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="pane">
      <div className="pane-eyebrow">
        <CheckCircleIcon size={11} /> ATS Compatibility
      </div>
      <h1 className="pane-title">ATS Resume Checker</h1>

      <div className="pane-card">
        <div className="pane-field">
          <label className="pane-label">Job description *</label>
          <textarea
            className="pane-textarea"
            placeholder="Paste the JD. Required by every keyword & format check below."
            value={jobDescription}
            onChange={e => setJobDescription(e.target.value)}
            rows={6}
          />
        </div>

        <div className="pane-field">
          <label className="pane-label">Resume *</label>
          {/* Three cards side-by-side via auto-fit grid — same shared
              styling as the other Electron setup panes. */}
          <div className="es-resume-choices">
            <ResumeChoiceCard
              checked={resumeChoice === 'profile'}
              disabled={!profile.hasResume}
              onClick={() => profile.hasResume && setResumeChoice('profile')}
              label="Use my profile data"
              status={
                profile.loading ? <span style={{ color: 'var(--es-text-subtle)' }}>(checking…)</span>
                : profile.hasResume ? (
                  <span className="es-choice-status ok"><CheckCircleIcon size={12} /> Loaded from profile</span>
                ) : (
                  <span className="es-choice-status warn"><AlertTriangleIcon size={12} /> profile is empty</span>
                )
              }
            />
            <ResumeChoiceCard
              checked={resumeChoice === 'upload'}
              onClick={() => setResumeChoice('upload')}
              label="Upload a resume file"
              status={uploadedFile && (
                <span className="es-choice-status ok">
                  <CheckCircleIcon size={12} /> {uploadedFile.name}
                  {isParsing && <span style={{ color: 'var(--es-text-subtle)', marginLeft: 6 }}>parsing…</span>}
                </span>
              )}
              action={resumeChoice === 'upload' && (
                <button type="button" className="es-choice-action" onClick={() => fileRef.current?.click()}>
                  {uploadedFile ? 'Replace file' : 'Choose file'}
                </button>
              )}
            />
            <ResumeChoiceCard
              checked={resumeChoice === 'paste'}
              onClick={() => setResumeChoice('paste')}
              label="Paste resume text"
              status={resumeChoice === 'paste' && pasteText.trim() && (
                <span className="es-choice-status ok">
                  <CheckCircleIcon size={12} /> {pasteText.trim().split(/\s+/).length} words
                </span>
              )}
            />
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" onChange={onFile} style={{ display: 'none' }} />
          {parseError && <div className="pane-error">{parseError}</div>}
          {resumeChoice === 'paste' && (
            <textarea
              className="pane-textarea"
              placeholder="Paste your resume text here…"
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              rows={6}
            />
          )}
        </div>

        {error && <div className="pane-error">{error}</div>}
      </div>

      <button
        type="button"
        className="pane-cta"
        onClick={handleAnalyze}
        disabled={!canStart || analyzing || gate.locked}
        title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
      >
        {analyzing
          ? 'Analyzing…'
          : <>Run ATS Check <SparklesIcon size={13} /> <FreeCreditsLabel feature="atsCheck" /></>}
      </button>
    </div>
  );
}

function ResumeChoiceCard({ checked, disabled, onClick, label, status, action }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`es-choice ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <span className="es-choice-radio" aria-hidden="true">
        <span className="es-choice-radio-dot" />
      </span>
      <div className="es-choice-body">
        <div className="es-choice-label">{label}</div>
        {status && <div className="es-choice-meta">{status}</div>}
        {action && <div className="es-choice-actions">{action}</div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* ResultsView                                                             */
/* ─────────────────────────────────────────────────────────────────────── */

function ResultsView({ result, meta, onRunAgain, onReset }) {
  if (!result) {
    return (
      <div className="pane">
        <h1 className="pane-title">No analysis yet</h1>
        <button className="pane-cta" onClick={onReset}>Run ATS Check →</button>
      </div>
    );
  }

  const {
    overallScore = 0,
    keywordMatches = [],
    missingKeywords = [],
    totalKeywords = 0,
    checklist = {},
    formatScore = 8,
    formatRecommendations = [],
    improvementSuggestions = [],
  } = result;

  const matchedCount = keywordMatches.length;
  const matchPct = totalKeywords > 0 ? Math.round((matchedCount / totalKeywords) * 100) : 0;
  const tone = overallScore >= 80 ? 'good' : overallScore >= 60 ? 'mid' : 'low';

  const checklistItems = Object.keys(CHECKLIST_LABELS).map(key => ({
    key,
    label: CHECKLIST_LABELS[key],
    present: !!checklist[key],
    tip: CHECKLIST_TIPS[key],
  }));
  const checklistDoneCount = checklistItems.filter(i => i.present).length;

  return (
    <div className="atc-root">
      {/* Score header */}
      <div className="atc-summary">
        <div className="atc-summary-head">
          <div>
            <div className="pane-eyebrow"><CheckCircleIcon size={11} /> Analyzed</div>
            <h1 className="pane-title" style={{ marginTop: 8 }}>ATS Compatibility Report</h1>
            <p className="pane-subtitle" style={{ marginBottom: 0 }}>
              {totalKeywords > 0
                ? `${matchedCount} of ${totalKeywords} keywords matched · ${matchPct}% coverage`
                : 'Keyword extraction unavailable — using format & structure scoring.'}
            </p>
          </div>
          <div className="atc-summary-actions">
            <button type="button" className="pane-cta-secondary" onClick={onRunAgain}>
              Run again
            </button>
            <button type="button" className="pane-cta-secondary" onClick={onReset}>
              Start over
            </button>
          </div>
        </div>

        <div className="atc-summary-grid">
          <ScoreCircle value={overallScore} label="Overall" tone={tone} />
          <Stat
            label="Keyword match"
            value={`${matchPct}%`}
            sub={totalKeywords > 0 ? `${matchedCount} / ${totalKeywords}` : '—'}
            tone={matchPct >= 70 ? 'good' : matchPct >= 40 ? 'mid' : 'low'}
          />
          <Stat
            label="Format score"
            value={`${formatScore}/10`}
            sub={formatScore >= 8 ? 'ATS-friendly' : formatScore >= 6 ? 'Decent' : 'Needs work'}
            tone={formatScore >= 8 ? 'good' : formatScore >= 6 ? 'mid' : 'low'}
          />
          <Stat
            label="Sections present"
            value={`${checklistDoneCount}/${checklistItems.length}`}
            sub={checklistDoneCount === checklistItems.length ? 'All key sections' : 'Some missing'}
            tone={checklistDoneCount === checklistItems.length ? 'good' : 'mid'}
          />
        </div>
      </div>

      {/* Keyword analysis */}
      {(keywordMatches.length > 0 || missingKeywords.length > 0) && (
        <div className="atc-keyword-block">
          <div className="atc-section-head">
            <h3>Keyword Analysis</h3>
            <span className="atc-section-meta">{matchedCount} matched · {missingKeywords.length} missing</span>
          </div>

          {keywordMatches.length > 0 && (
            <div className="atc-kw-group">
              <div className="atc-kw-group-label ok">
                <CheckCircleIcon size={12} /> In your resume
              </div>
              <div className="atc-kw-list">
                {keywordMatches.map(k => (
                  <span key={k} className="atc-kw matched">{k}</span>
                ))}
              </div>
            </div>
          )}

          {missingKeywords.length > 0 && (
            <div className="atc-kw-group">
              <div className="atc-kw-group-label warn">
                <AlertTriangleIcon size={12} /> Missing — consider adding
              </div>
              <div className="atc-kw-list">
                {missingKeywords.map(k => (
                  <span key={k} className="atc-kw missing">{k}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section checklist */}
      <div className="atc-checklist-block">
        <div className="atc-section-head">
          <h3>Section Checklist</h3>
          <span className="atc-section-meta">{checklistDoneCount}/{checklistItems.length}</span>
        </div>
        <div className="atc-checklist">
          {checklistItems.map(item => (
            <div key={item.key} className={`atc-check ${item.present ? 'ok' : 'missing'}`}>
              <div className="atc-check-icon">
                {item.present ? <CheckCircleIcon size={14} /> : <XIcon size={14} />}
              </div>
              <div className="atc-check-content">
                <div className="atc-check-label">{item.label}</div>
                {!item.present && <div className="atc-check-tip">{item.tip}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Format recommendations */}
      {formatRecommendations.length > 0 && (
        <div className="atc-list-block">
          <div className="atc-section-head">
            <h3>Format Recommendations</h3>
          </div>
          <ul className="atc-bullet-list">
            {formatRecommendations.map((rec, i) => (
              <li key={i}>
                <SparklesIcon size={13} />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Improvement suggestions */}
      {improvementSuggestions.length > 0 && (
        <div className="atc-list-block">
          <div className="atc-section-head">
            <h3>Top Improvements</h3>
            <span className="atc-section-meta">Apply these first</span>
          </div>
          <ul className="atc-bullet-list">
            {improvementSuggestions.map((s, i) => (
              <li key={i}>
                <HelpIcon size={13} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ScoreCircle({ value, label, tone = 'mid' }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const circ = 2 * Math.PI * 38;
  const dash = (pct / 100) * circ;
  const color = tone === 'good' ? 'var(--es-success)' : tone === 'low' ? 'var(--es-danger)' : 'var(--es-warning)';
  return (
    <div className="atc-score-circle">
      <svg viewBox="0 0 100 100" width="110" height="110" aria-hidden="true">
        <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" />
        <circle
          cx="50" cy="50" r="38" fill="none"
          stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="atc-score-value">{pct}<span>%</span></div>
      <div className="atc-score-label">{label}</div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`atc-stat ${tone || ''}`}>
      <div className="atc-stat-label">{label}</div>
      <div className="atc-stat-value">{value}</div>
      {sub && <div className="atc-stat-sub">{sub}</div>}
    </div>
  );
}
